import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Search, Save, X, AlertCircle, CheckCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'

function Admin({ user }) {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [editingProduct, setEditingProduct] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  useEffect(() => {
    checkAdminStatus()
    fetchProducts()
  }, [user])

  const checkAdminStatus = async () => {
    const { data, error } = await supabase
      .from('tobacco_admin_roles')
      .select('*')
      .eq('user_id', user.id)
      .single()

    if (data && !error) {
      setIsAdmin(true)
    }
  }

  const fetchProducts = async () => {
    setLoading(true)
    try {
      const trimmedQuery = searchQuery.trim()
      const isNumeric = trimmedQuery && /^\d+$/.test(trimmedQuery)
      
      let query = supabase
        .from('tobacco_products')
        .select('*')

      if (trimmedQuery) {
        if (isNumeric) {
          query = query.or(`small_box_barcode.eq.${trimmedQuery},carton_barcode.eq.${trimmedQuery}`)
        } else {
          query = query.ilike('name', `%${trimmedQuery}%`)
        }
      }
      
      query = query.order('created_at', { ascending: false })

      const { data, error } = await query.limit(100)

      if (error) throw error
      setProducts(data || [])
    } catch (error) {
      showMessage('error', `获取数据失败: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const showMessage = (type, text) => {
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: '', text: '' }), 3000)
  }

  const handleCreate = () => {
    setIsCreating(true)
    setEditingProduct({
      name: '',
      product_type: '烤烟型',
      tar_content: null,
      nicotine_content: null,
      carbon_monoxide_content: null,
      packaging_form: '',
      cigarette_spec: '84mm',
      cigarette_length: '84mm',
      cigarette_type: '',
      cigarette_feature: '',
      small_box_barcode: '',
      carton_barcode: '',
      small_box_price: '',
      carton_price: '',
      wholesale_price: '',
      launch_time: '',
      image_local_path: ''
    })
  }

  const handleEdit = (product) => {
    setEditingProduct({ ...product })
    setIsCreating(false)
  }

  const handleSave = async () => {
    if (!editingProduct.name || !editingProduct.small_box_barcode) {
      showMessage('error', '请填写必填字段：名称和小盒条码')
      return
    }

    try {
      const productData = {
        ...editingProduct,
        updated_at: new Date().toISOString()
      }

      let result
      if (isCreating) {
        result = await supabase
          .from('tobacco_products')
          .insert([productData])
      } else {
        result = await supabase
          .from('tobacco_products')
          .update(productData)
          .eq('id', editingProduct.id)
      }

      if (result.error) throw result.error

      showMessage('success', isCreating ? '创建成功！' : '更新成功！')
      setEditingProduct(null)
      setIsCreating(false)
      fetchProducts()
    } catch (error) {
      showMessage('error', `保存失败: ${error.message}`)
    }
  }

  const handleDelete = async (productId) => {
    if (!window.confirm('确定要删除此产品吗？此操作不可撤销。')) return

    try {
      const { error } = await supabase
        .from('tobacco_products')
        .delete()
        .eq('id', productId)

      if (error) throw error

      showMessage('success', '删除成功！')
      fetchProducts()
    } catch (error) {
      showMessage('error', `删除失败: ${error.message}`)
    }
  }

  const handleCancel = () => {
    setEditingProduct(null)
    setIsCreating(false)
  }

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="card-static p-8 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">权限不足</h2>
          <p className="text-gray-600">您没有管理员权限，无法访问此页面。</p>
          <p className="text-sm text-gray-500 mt-2">请联系系统管理员申请管理员权限。</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">烟草信息管理</h1>
        <p className="text-gray-600">管理所有烟草产品信息</p>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-2xl flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 mr-2" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-2" />
          )}
          {message.text}
        </div>
      )}

      <div className="card-static p-6 mb-6">
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchProducts()}
              placeholder="搜索烟草名称或条码..."
              className="input-solar pl-10 py-3 w-full"
            />
          </div>
          <button
            onClick={fetchProducts}
            className="btn-primary px-6 py-3"
          >
            搜索
          </button>
          <button
            onClick={handleCreate}
            className="btn-secondary px-6 py-3 flex items-center justify-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            新增产品
          </button>
        </div>
      </div>

      {editingProduct && (
        <div className="card-static p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              {isCreating ? '新增产品' : '编辑产品'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="产品名称 *"
              value={editingProduct.name}
              onChange={(v) => setEditingProduct({ ...editingProduct, name: v })}
            />
            <FormField
              label="产品类型"
              value={editingProduct.product_type}
              onChange={(v) => setEditingProduct({ ...editingProduct, product_type: v })}
              type="select"
              options={['烤烟型', '混合型', '外香型', '雪茄型']}
            />
            <FormField
              label="焦油含量 (mg)"
              value={editingProduct.tar_content}
              onChange={(v) => setEditingProduct({ ...editingProduct, tar_content: v })}
              type="number"
            />
            <FormField
              label="烟碱含量 (mg)"
              value={editingProduct.nicotine_content}
              onChange={(v) => setEditingProduct({ ...editingProduct, nicotine_content: v })}
              type="number"
            />
            <FormField
              label="一氧化碳含量 (mg)"
              value={editingProduct.carbon_monoxide_content}
              onChange={(v) => setEditingProduct({ ...editingProduct, carbon_monoxide_content: v })}
              type="number"
            />
            <FormField
              label="小盒条码 *"
              value={editingProduct.small_box_barcode}
              onChange={(v) => setEditingProduct({ ...editingProduct, small_box_barcode: v })}
            />
            <FormField
              label="条盒条码"
              value={editingProduct.carton_barcode}
              onChange={(v) => setEditingProduct({ ...editingProduct, carton_barcode: v })}
            />
            <FormField
              label="小盒价格"
              value={editingProduct.small_box_price}
              onChange={(v) => setEditingProduct({ ...editingProduct, small_box_price: v })}
            />
            <FormField
              label="条盒价格"
              value={editingProduct.carton_price}
              onChange={(v) => setEditingProduct({ ...editingProduct, carton_price: v })}
            />
            <FormField
              label="批发价格"
              value={editingProduct.wholesale_price}
              onChange={(v) => setEditingProduct({ ...editingProduct, wholesale_price: v })}
            />
            <FormField
              label="包装形式"
              value={editingProduct.packaging_form}
              onChange={(v) => setEditingProduct({ ...editingProduct, packaging_form: v })}
            />
            <FormField
              label="烟支规格"
              value={editingProduct.cigarette_spec}
              onChange={(v) => setEditingProduct({ ...editingProduct, cigarette_spec: v })}
            />
            <FormField
              label="烟支类型"
              value={editingProduct.cigarette_type}
              onChange={(v) => setEditingProduct({ ...editingProduct, cigarette_type: v })}
              type="select"
              options={['', '中支', '细支', '粗支']}
            />
            <FormField
              label="上市时间"
              value={editingProduct.launch_time}
              onChange={(v) => setEditingProduct({ ...editingProduct, launch_time: v })}
            />
          </div>

          <div className="mt-4">
            <FormField
              label="产品特点"
              value={editingProduct.cigarette_feature}
              onChange={(v) => setEditingProduct({ ...editingProduct, cigarette_feature: v })}
              type="textarea"
            />
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              className="px-6 py-3 border border-gray-300 rounded-2xl text-gray-700 hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              className="btn-primary px-6 py-3 flex items-center"
            >
              <Save className="w-5 h-5 mr-2" />
              保存
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-green-400 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="card-static p-8 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">暂无数据</p>
        </div>
      ) : (
        <div className="card-static overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-green-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">名称</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">类型</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">小盒条码</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">小盒价格</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">焦油</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-green-100">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-green-50/50 transition-colors">
                    <td className="px-4 py-3 text-gray-800">{product.name}</td>
                    <td className="px-4 py-3 text-gray-600">{product.product_type}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-sm">{product.small_box_barcode}</td>
                    <td className="px-4 py-3 text-gray-600">{product.small_box_price}</td>
                    <td className="px-4 py-3 text-gray-600">{product.tar_content ? `${product.tar_content}mg` : '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function FormField({ label, value, onChange, type = 'text', options = [] }) {
  if (type === 'select') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="input-solar py-2 px-3"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    )
  }

  if (type === 'textarea') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="input-solar py-2 px-3 min-h-[100px]"
          rows={3}
        />
      </div>
    )
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) : e.target.value)}
        className="input-solar py-2 px-3"
      />
    </div>
  )
}

function Package({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  )
}

export default Admin
